/**
 * The card document's invariants, in one pure function.
 *
 * Every reducer case funnels its result through `normalizeCard`, so the rules
 * below hold after *any* mutation rather than being re-argued per action:
 *
 * 1. `fieldOrder` lists all 29 fields exactly once (spec R36, R41).
 * 2. Exactly one placement per enabled field that renders a block, none for a
 *    disabled field, and none for a field in `BLOCKLESS_FIELD_IDS` — the Primary
 *    Image is the photo background, and the UOM field only suffixes the prices
 *    (spec R72).
 * 3. `placement.order` is derived from `fieldOrder`, so the rail order and the
 *    Info order can never disagree (spec R47 holds by construction).
 * 4. No placement sits in the Floating region while the footer is off; such a
 *    block returns to Info instead of disappearing (spec R53).
 * 5. A `rowKey` survives only where two adjacent Info placements share it —
 *    a lone paired block goes back to being its own row (spec R51).
 * 6. User-typed text is length-capped (threat model, abuse case 2).
 *
 * No React, no storage, no clock in this file.
 */

import {
  CTA_FIELD_ID,
  CTA_HEIGHT_PX_RANGE,
  PROPERTY_SECTIONS,
  CTA_WIDTH_PCT_RANGE,
  DEFAULT_CTA_CONFIGURATION,
  DEFAULT_FIELD_ORDER,
  DEFAULT_PHOTO_ANCHOR,
  DESC_FIELD_ID,
  FLOATING_ANCHOR,
  IMAGE_FIELD_ID,
  MAX_CARD_NAME_LENGTH,
  MAX_CTA_LABEL_LENGTH,
  MAX_CUSTOM_CSS_LENGTH,
  MAX_DISPLAY_LABEL_LENGTH,
  clampToRange,
  getDefaultFieldConfiguration,
  getSuggestedPlacement,
  isDescriptionStyle,
  isImageFit,
  rendersBlock,
} from '../constants';
import {
  BlockPlacement,
  CtaConfiguration,
  FieldId,
  FieldConfiguration,
  InfoRow,
  ProductCard,
  PropertySectionKind,
} from '../types';

/** Caps a user-typed string without trimming, so typing a space still works. */
export const clampText = (value: string, maxLength: number): string =>
  value.length > maxLength ? value.slice(0, maxLength) : value;

const dedupe = (fieldIds: FieldId[]): FieldId[] => {
  const seen = new Set<FieldId>();
  const out: FieldId[] = [];
  fieldIds.forEach((fieldId) => {
    if (!seen.has(fieldId)) {
      seen.add(fieldId);
      out.push(fieldId);
    }
  });
  return out;
};

/**
 * All 29 fields exactly once: the stored order first, then anything missing —
 * and the Primary Image always at index 0.
 *
 * **The image is pinned (2026-09-29).** It cannot be dragged (`supportsReorder:
 * false`), but a disabled `Draggable` still occupies an index, so until now any
 * other row could be dropped *above* it and the image stopped being first. It
 * is the photo region rather than a block, so a card whose list opens with
 * "Model Number" above it misdescribes what the card renders.
 *
 * Pinned HERE rather than in the drag handler because this is the one funnel:
 * every reducer case ends in `normalizeCard`, and so does every load. That
 * covers the drag, any future mutation, and a card stored before this rule
 * existed with the image somewhere in the middle — which is repaired on read
 * rather than needing a migration.
 *
 * Nothing else shifts: `placement.order` is derived from the *relative* order
 * of the placed fields, and the image has no placement, so moving it to the
 * front changes no block's position on the card.
 */
const completeFieldOrder = (fieldOrder: FieldId[]): FieldId[] => {
  const ordered = dedupe(fieldOrder);
  const present = new Set(ordered);
  DEFAULT_FIELD_ORDER.forEach((fieldId) => {
    if (!present.has(fieldId)) {
      ordered.push(fieldId);
    }
  });
  return [IMAGE_FIELD_ID, ...ordered.filter((fieldId) => fieldId !== IMAGE_FIELD_ID)];
};

export const buildFieldOrderIndex = (fieldOrder: FieldId[]): Map<FieldId, number> =>
  new Map(fieldOrder.map((fieldId, index) => [fieldId, index]));

const byFieldOrder =
  (index: Map<FieldId, number>) =>
  (left: BlockPlacement, right: BlockPlacement): number =>
    (index.get(left.fieldId) ?? 0) - (index.get(right.fieldId) ?? 0);

/**
 * Groups consecutive Info placements that share a `rowKey` into one rendered
 * row. Placements are expected pre-sorted by `fieldOrder`.
 */
export const groupInfoRows = (placements: BlockPlacement[]): InfoRow[] => {
  const rows: InfoRow[] = [];
  placements.forEach((placement) => {
    const previous = rows[rows.length - 1];
    if (
      previous &&
      placement.rowKey !== undefined &&
      previous.rowKey === placement.rowKey
    ) {
      previous.placements.push(placement);
      return;
    }
    rows.push({
      rowKey: placement.rowKey ?? `single:${placement.fieldId}`,
      placements: [placement],
    });
  });
  return rows;
};

/**
 * Drops a `rowKey` that no longer pairs anything: a group of one, or a repeat of
 * a key already claimed by an earlier group.
 */
const pruneRowKeys = (placements: BlockPlacement[]): BlockPlacement[] => {
  const rows = groupInfoRows(placements);
  const claimed = new Set<string>();
  const out: BlockPlacement[] = [];

  rows.forEach((row) => {
    const key = row.placements[0].rowKey;
    const isRealPair = row.placements.length > 1 && key !== undefined && !claimed.has(key);
    if (isRealPair && key !== undefined) {
      claimed.add(key);
      row.placements.forEach((placement) => out.push({ ...placement, rowKey: key }));
      return;
    }
    row.placements.forEach((placement) => {
      out.push({
        fieldId: placement.fieldId,
        region: placement.region,
        order: placement.order,
        anchor: placement.anchor,
      });
    });
  });

  return out;
};

/**
 * The CTA's options, every one of them in range (spec R70).
 *
 * Exported because `services/parseStoredCards.ts` needs the same clamp on the same
 * shape: a stored `widthPct` of `1e9` has to become something the slider can
 * represent, whether it arrived from a dispatch or from storage.
 */
export const normalizeCtaConfiguration = (
  stored: Partial<CtaConfiguration> | undefined
): CtaConfiguration => ({
  label: clampText(
    typeof stored?.label === 'string' ? stored.label : DEFAULT_CTA_CONFIGURATION.label,
    MAX_CTA_LABEL_LENGTH
  ),
  showText: typeof stored?.showText === 'boolean' ? stored.showText : DEFAULT_CTA_CONFIGURATION.showText,
  showIcon: typeof stored?.showIcon === 'boolean' ? stored.showIcon : DEFAULT_CTA_CONFIGURATION.showIcon,
  fullWidth:
    typeof stored?.fullWidth === 'boolean' ? stored.fullWidth : DEFAULT_CTA_CONFIGURATION.fullWidth,
  widthPct: clampToRange(stored?.widthPct, CTA_WIDTH_PCT_RANGE),
  heightPx: clampToRange(stored?.heightPx, CTA_HEIGHT_PX_RANGE),
});

/**
 * Rebuilds every field's configuration key by key.
 *
 * Deliberately not a spread of `stored`: each key states where it is allowed to
 * appear and what values it accepts, so a value that has drifted out of range —
 * from an older build, from devtools, from a future API — is corrected here rather
 * than reaching the preview. The cost is that **a new key on `FieldConfiguration` must
 * be added here too**, or it is dropped on the first dispatch; see the interface's
 * own note.
 */
/**
 * Does this field's properties panel offer `section`?
 *
 * The membership question `FieldConfigurationPanel` asks of the same table, used
 * here to decide which optional settings the stored document spells out.
 */
const offersSection = (fieldId: FieldId, section: PropertySectionKind): boolean =>
  PROPERTY_SECTIONS[fieldId].includes(section);

const normalizeConfiguration = (
  configuration: Partial<Record<FieldId, FieldConfiguration>>,
  fieldOrder: FieldId[]
): Partial<Record<FieldId, FieldConfiguration>> => {
  const out: Partial<Record<FieldId, FieldConfiguration>> = {};
  fieldOrder.forEach((fieldId) => {
    const stored = configuration[fieldId];
    if (!stored) {
      return;
    }
    const customCss = typeof stored.customCss === 'string' ? stored.customCss : '';
    out[fieldId] = {
      displayLabel: clampText(stored.displayLabel, MAX_DISPLAY_LABEL_LENGTH),
      alignment: stored.alignment,
      // Only the image carries a fit, and only a value from the enum survives —
      // anything else falls back to the documented default at read time.
      imageFit:
        fieldId === IMAGE_FIELD_ID && isImageFit(stored.imageFit) ? stored.imageFit : undefined,
      // Every element may carry custom CSS (R67), capped but never rewritten: the
      // counter counts what the user typed, and confinement happens in `styling/`.
      customCss: customCss === '' ? undefined : clampText(customCss, MAX_CUSTOM_CSS_LENGTH),
      // MATERIALISED, not implied (operator, 2026-09-28). A field carries the
      // boolean settings ITS OWN panel offers, written out as `false` when off
      // rather than left absent — so the stored document lists every option the
      // user can see, and a reader of the JSON does not have to know that a
      // missing key means off.
      //
      // Keyed off `PROPERTY_SECTIONS`, the same table `FieldConfigurationPanel`
      // renders from, so the keys in the document and the controls on the screen
      // cannot drift apart: give a field the Pricing section and it gains
      // `showUom`; give it Strikethrough and it gains `strikethrough`; a field
      // with neither (every non-price field) still carries neither.
      //
      // Read directly rather than through `selectors/selectPropertySections`
      // (ADR-4B's "one decider") only because `selectors/` imports `state/` —
      // going the other way would close an import cycle. Same table, same answer.
      strikethrough: offersSection(fieldId, 'strikethrough')
        ? stored.strikethrough === true
        : undefined,
      showUom: offersSection(fieldId, 'pricing') ? stored.showUom === true : undefined,
      // Description style belongs to the description, the CTA options to the CTA.
      descStyle:
        fieldId === DESC_FIELD_ID && isDescriptionStyle(stored.descStyle)
          ? stored.descStyle
          : undefined,
      cta:
        fieldId === CTA_FIELD_ID && stored.cta !== undefined
          ? normalizeCtaConfiguration(stored.cta)
          : undefined,
    };
  });
  return out;
};

/**
 * A placement for every enabled field **that renders a block**, with the footer rule
 * applied and the region-specific extras (anchor, rowKey) kept only where they mean
 * something.
 *
 * The Primary Image is the photo background and the UOM field's whole effect is a
 * suffix on the price rows (spec R72) — neither gets a placement, so no region can
 * render an empty draggable shell for them. `BLOCKLESS_FIELD_IDS` is the one place
 * that list lives.
 */
const completePlacements = (
  existing: BlockPlacement[],
  enabledFieldIds: FieldId[],
  footerEnabled: boolean
): BlockPlacement[] => {
  const enabled = new Set(enabledFieldIds);
  const byFieldId = new Map<FieldId, BlockPlacement>();

  existing.forEach((placement) => {
    if (!rendersBlock(placement.fieldId)) return;
    if (!enabled.has(placement.fieldId)) return;
    if (byFieldId.has(placement.fieldId)) return;
    byFieldId.set(placement.fieldId, placement);
  });

  enabledFieldIds.forEach((fieldId) => {
    if (!rendersBlock(fieldId) || byFieldId.has(fieldId)) return;
    const suggested = getSuggestedPlacement(fieldId);
    byFieldId.set(fieldId, {
      fieldId,
      region: suggested.region,
      order: 0,
      anchor: suggested.anchor,
    });
  });

  return Array.from(byFieldId.values()).map((placement) => {
    // Floating is the footer strip; with no footer the block belongs in Info.
    if (placement.region === 'floating' && !footerEnabled) {
      return { fieldId: placement.fieldId, region: 'info', order: 0 };
    }
    if (placement.region === 'floating') {
      return {
        fieldId: placement.fieldId,
        region: 'floating',
        order: 0,
        anchor: FLOATING_ANCHOR,
      };
    }
    if (placement.region === 'photo') {
      return {
        fieldId: placement.fieldId,
        region: 'photo',
        order: 0,
        anchor: placement.anchor === FLOATING_ANCHOR || placement.anchor === undefined
          ? DEFAULT_PHOTO_ANCHOR
          : placement.anchor,
      };
    }
    return {
      fieldId: placement.fieldId,
      region: 'info',
      order: 0,
      rowKey: placement.rowKey,
    };
  });
};

/** Renumbers `order` within each region — and within each photo anchor. */
const renumber = (placements: BlockPlacement[]): BlockPlacement[] => {
  const counters = new Map<string, number>();
  return placements.map((placement) => {
    const bucket =
      placement.region === 'photo'
        ? `photo:${placement.anchor ?? DEFAULT_PHOTO_ANCHOR}`
        : placement.region;
    const next = counters.get(bucket) ?? 0;
    counters.set(bucket, next + 1);
    return { ...placement, order: next };
  });
};

export const normalizeCard = (card: ProductCard): ProductCard => {
  const fieldOrder = completeFieldOrder(card.fieldOrder);
  const orderIndex = buildFieldOrderIndex(fieldOrder);
  const enabledFieldIds = dedupe(card.enabledFieldIds).sort(
    (left, right) => (orderIndex.get(left) ?? 0) - (orderIndex.get(right) ?? 0)
  );

  const placements = completePlacements(
    card.placements,
    enabledFieldIds,
    card.footerEnabled
  ).sort(byFieldOrder(orderIndex));

  const infoPlacements = pruneRowKeys(placements.filter((p) => p.region === 'info'));
  const otherPlacements = placements.filter((p) => p.region !== 'info');

  return {
    id: card.id,
    name: clampText(card.name, MAX_CARD_NAME_LENGTH),
    orientation: card.orientation,
    footerEnabled: card.footerEnabled,
    fieldOrder,
    enabledFieldIds,
    placements: renumber(
      [...infoPlacements, ...otherPlacements].sort(byFieldOrder(orderIndex))
    ),
    configuration: normalizeConfiguration(card.configuration, fieldOrder),
    references: card.references,
    updatedAt: card.updatedAt,
  };
};

/** Configuration for one field, falling back to the documented defaults (R43, R44). */
export const getFieldConfiguration = (card: ProductCard, fieldId: FieldId): FieldConfiguration =>
  card.configuration[fieldId] ?? getDefaultFieldConfiguration(fieldId);

/**
 * Moves a field so it sits immediately before `beforeFieldId` in the rail order
 * (`null` sends it to the end). This is the only way sequence changes, which is
 * what keeps the rail and the preview in lockstep.
 */
export const moveFieldBefore = (
  fieldOrder: FieldId[],
  fieldId: FieldId,
  beforeFieldId: FieldId | null
): FieldId[] => {
  if (beforeFieldId === fieldId) {
    return fieldOrder;
  }
  const without = fieldOrder.filter((candidate) => candidate !== fieldId);
  if (beforeFieldId === null) {
    return [...without, fieldId];
  }
  const target = without.indexOf(beforeFieldId);
  if (target === -1) {
    return [...without, fieldId];
  }
  return [...without.slice(0, target), fieldId, ...without.slice(target)];
};

export const moveFieldAfter = (
  fieldOrder: FieldId[],
  fieldId: FieldId,
  afterFieldId: FieldId
): FieldId[] => {
  if (afterFieldId === fieldId) {
    return fieldOrder;
  }
  const without = fieldOrder.filter((candidate) => candidate !== fieldId);
  const target = without.indexOf(afterFieldId);
  if (target === -1) {
    return [...without, fieldId];
  }
  return [...without.slice(0, target + 1), fieldId, ...without.slice(target + 1)];
};

/**
 * Newly enabled fields stack above the Primary CTA button so Add to Cart stays
 * pinned at the bottom of its region (spec R39). The user can still drag past
 * it afterwards.
 */
export const keepCtaLast = (
  fieldOrder: FieldId[],
  fieldId: FieldId,
  ctaEnabled: boolean
): FieldId[] => {
  if (fieldId === CTA_FIELD_ID || !ctaEnabled) {
    return fieldOrder;
  }
  const ctaIndex = fieldOrder.indexOf(CTA_FIELD_ID);
  const fieldIndex = fieldOrder.indexOf(fieldId);
  if (ctaIndex === -1 || fieldIndex === -1 || fieldIndex < ctaIndex) {
    return fieldOrder;
  }
  return moveFieldBefore(fieldOrder, fieldId, CTA_FIELD_ID);
};
